import { useState, type FormEvent, type KeyboardEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Check, Lightbulb, MousePointerClick, Star, ThumbsUp, Wrench, type LucideIcon } from 'lucide-react'
import { Button, Modal, Select, Textarea } from '@/components/ui'
import { ApiError } from '@/internationalization/api-client'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import { toast } from '@/stores'
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_TYPES,
  MAX_COMMENTS,
  feedbackApi,
  type FeedbackCategory,
  type FeedbackType,
} from './api'

const TYPE_ICONS: Record<FeedbackType, LucideIcon> = {
  like: ThumbsUp,
  improve: Wrench,
  feature_request: Lightbulb,
  usability: MousePointerClick,
}

const TYPE_LABELS: Record<FeedbackType, string> = {
  like: 'feedback.type.like',
  improve: 'feedback.type.improve',
  feature_request: 'feedback.type.feature_request',
  usability: 'feedback.type.usability',
}

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  recording: 'feedback.category.recording',
  transcript: 'feedback.category.transcript',
  summary: 'feedback.category.summary',
  action_items: 'feedback.category.action_items',
  search_ask: 'feedback.category.search_ask',
  analytics: 'feedback.category.analytics',
  integrations: 'feedback.category.integrations',
  settings: 'feedback.category.settings',
  other: 'feedback.category.other',
}

/**
 * Arrow-key movement for a `role=radio` group, as the ARIA pattern promises:
 * the arrows move to the neighbouring option (wrapping), select it and focus
 * it. Pair with a roving tabindex so the group is a single Tab stop.
 */
function moveRadio(event: KeyboardEvent<HTMLElement>, index: number, count: number, select: (next: number) => void) {
  const step =
    event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
    : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1
    : 0
  if (step === 0) return
  event.preventDefault()
  const next = (index + step + count) % count
  select(next)
  const radios = event.currentTarget.parentElement?.querySelectorAll<HTMLElement>('[role="radio"]')
  radios?.[next]?.focus()
}

/**
 * A short form in the existing Modal: kind of feedback, the part of Sabha it
 * is about, an optional rating and optional words. Only the first two are
 * required, because a form that demands an essay collects fewer submissions,
 * not better ones.
 *
 * Mounted only while open, so every opening starts blank without a reset
 * effect; the Modal restores focus to the button on unmount.
 */
export function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const t = useT()
  const [type, setType] = useState<FeedbackType | null>(null)
  const [category, setCategory] = useState<FeedbackCategory | ''>('')
  const [rating, setRating] = useState(0)
  const [comments, setComments] = useState('')

  const submit = useMutation({
    mutationFn: () =>
      feedbackApi.submit({
        type: type as FeedbackType,
        category: category as FeedbackCategory,
        rating: rating || undefined,
        comments: comments.trim() || undefined,
        // Path only. The query string can carry a share token or an SSO code.
        pagePath: window.location.pathname,
      }),
    onSuccess: () => {
      toast.success(t('feedback.toast.sent'), t('feedback.toast.sentBody'))
      onClose()
    },
  })

  const ready = type !== null && category !== ''

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (ready && !submit.isPending) submit.mutate()
  }

  const failure =
    submit.error instanceof ApiError && submit.error.status === 400
      ? submit.error.message
      : submit.error instanceof ApiError && submit.error.status === 429
        ? t('feedback.rateLimited')
        : submit.error
          ? t('feedback.error')
          : null

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('feedback.title')}
      description={t('feedback.description')}
      blurBackdrop
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="feedback-form"
            variant="primary"
            isLoading={submit.isPending}
            disabled={!ready}
          >
            {t('feedback.submit')}
          </Button>
        </>
      }
    >
      <form id="feedback-form" onSubmit={onSubmit} className="space-y-5">
        <fieldset>
          <legend className="mb-1.5 text-sm font-medium text-fg">{t('feedback.typeLabel')}</legend>
          <div
            role="radiogroup"
            aria-label={t('feedback.typeLabel')}
            className="grid grid-cols-1 gap-2 min-[400px]:grid-cols-2"
          >
            {FEEDBACK_TYPES.map((option, index) => {
              const Icon = TYPE_ICONS[option]
              const selected = type === option
              return (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  tabIndex={selected || (type === null && index === 0) ? 0 : -1}
                  onKeyDown={(event) => moveRadio(event, index, FEEDBACK_TYPES.length, (next) => setType(FEEDBACK_TYPES[next]))}
                  onClick={() => setType(option)}
                  className={cn(
                    'group flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-3 text-left text-base transition duration-fast active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100',
                    selected
                      ? 'border-brand bg-brand-subtle font-medium text-fg shadow-md ring-2 ring-brand'
                      : 'border-border bg-surface text-fg-muted shadow-xs hover:-translate-y-0.5 hover:border-brand hover:bg-brand-subtle hover:text-fg hover:shadow-md motion-reduce:hover:translate-y-0',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'grid h-8 w-8 shrink-0 place-items-center rounded-md transition-colors duration-fast',
                      selected
                        ? 'bg-brand text-brand-fg'
                        : 'bg-surface-muted text-fg-subtle group-hover:bg-brand/15 group-hover:text-brand',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1 break-words">{t(TYPE_LABELS[option])}</span>
                  {selected && <Check aria-hidden className="h-4 w-4 shrink-0 text-brand" strokeWidth={2.5} />}
                </button>
              )
            })}
          </div>
        </fieldset>

        <Select
          label={t('feedback.categoryLabel')}
          placeholder={t('feedback.categoryPlaceholder')}
          value={category}
          onChange={(event) => setCategory(event.target.value as FeedbackCategory | '')}
          options={FEEDBACK_CATEGORIES.map((value) => ({ value, label: t(CATEGORY_LABELS[value]) }))}
        />

        <div>
          <span className="mb-1.5 block text-sm font-medium text-fg">
            {t('feedback.ratingLabel')}{' '}
            <span className="font-normal text-fg-subtle">{t('feedback.optional')}</span>
          </span>
          <div role="radiogroup" aria-label={t('feedback.ratingLabel')} className="flex gap-1">
            {[1, 2, 3, 4, 5].map((value) => {
              // Only a click (or arrow key) changes the fill: hover never does.
              const filled = value <= rating
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={rating === value}
                  aria-label={t('feedback.stars', { count: value })}
                  tabIndex={rating === value || (rating === 0 && value === 1) ? 0 : -1}
                  onKeyDown={(event) => moveRadio(event, value - 1, 5, (next) => setRating(next + 1))}
                  // Pressing the chosen star again clears it: the rating is optional.
                  onClick={() => setRating(rating === value ? 0 : value)}
                  className="cursor-pointer rounded p-1 text-fg-subtle transition-transform duration-fast hover:scale-110 motion-reduce:transition-none motion-reduce:hover:scale-100"
                >
                  <Star
                    aria-hidden
                    className={cn('h-6 w-6 transition-colors duration-fast', filled && 'fill-star text-star')}
                    strokeWidth={1.5}
                  />
                </button>
              )
            })}
          </div>
        </div>

        <Textarea
          label={t('feedback.commentsLabel')}
          placeholder={t('feedback.commentsPlaceholder')}
          rows={4}
          maxLength={MAX_COMMENTS}
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          hint={t('feedback.commentsCount', { used: comments.length, max: MAX_COMMENTS })}
        />

        {failure && (
          <p role="alert" className="text-sm text-danger">
            {failure}
          </p>
        )}
      </form>
    </Modal>
  )
}
